import type { MappedEntityType, MarkerSetupType } from '@core/core/types'

import type { MarkerParseResult } from './types'

export function parseMarkersTable(
  dataTable: string[][],
  settings: MarkerSetupType,
  mappings: MappedEntityType[],
): MarkerParseResult {
  const markersOnWells = new Set<string>()
  return dataTable.reduce<MarkerParseResult>(
    (result, dataRow, idx) => {
      const line = settings.missedLinesCount + idx
      const rowLen = dataRow.length

      const isIndexValid = (columnIndex: number, column: string) => {
        if (columnIndex < 1) {
          result.errors.push({
            line,
            column,
            error: 'Колонки должны начинаться с 1',
          })
          return false
        }
        if (columnIndex > rowLen) {
          result.errors.push({
            line,
            column,
            error: `В строке нет колонки с номером ${columnIndex}`,
          })
          return false
        }

        return true
      }

      const getString = (columnIndex: number, column: string) => {
        if (!isIndexValid(columnIndex, column)) {
          return undefined
        }
        const value = dataRow[columnIndex - 1]
        if (!value) {
          result.errors.push({ line, column, error: 'Значение отсутствует' })
          return undefined
        }

        return value
      }

      const getNumber = (columnIndex: number, column: string) => {
        if (!isIndexValid(columnIndex, column)) {
          return undefined
        }
        const value = dataRow[columnIndex - 1]
        if (!value) {
          result.errors.push({ line, column, error: 'Значение отсутствует' })
          return undefined
        }

        const number = +value.replace(',', '.')
        if (isNaN(number)) {
          result.errors.push({ line, column, error: `Значение ${value} не является числом` })
          return undefined
        }

        return number
      }

      if (rowLen < 3) {
        if (dataRow.join('').length > 0) {
          result.errors.push({ line, error: 'Слишком короткая строка' })
        }
        return result
      }

      let column = 'Well'
      const wellName = getString(settings.wellNameColumn, column)
      if (wellName === undefined) {
        return result
      }
      const mapping = mappings.find(mapping => mapping.name === wellName)
      if (mapping === undefined) {
        result.errors.push({ line, column, error: 'Привязка не найдена' })
        return result
      }
      const wellId = mapping.mappedTo?.id
      if (wellId === undefined) {
        // Об этом уже предупреждали, значит не ошибка, просто пропускаем
        // result.errors.push({ line, column, error: `Скважина ${wellName} не привязана` })
        return result
      }

      column = 'Name'
      const markerName = getString(settings.markerNameColumn, column)
      if (markerName === undefined) {
        return result
      }

      const markerOnWell = wellId + markerName
      if (markersOnWells.has(markerOnWell)) {
        result.errors.push({
          line,
          column,
          error: `Маркер ${markerName} на скважине ${mapping.name} уже определен`,
        })
        return result
      }
      markersOnWells.add(markerOnWell)

      column = 'Depth'
      const depth = getNumber(settings.markerBoreDepthColumn ?? 0, column)
      if (depth === undefined) {
        return result
      }

      let depthAbs: number | undefined
      if (settings.markerAbsDepthColumn !== undefined) {
        column = 'DepthAbs'
        depthAbs = getNumber(settings.markerAbsDepthColumn, column)
        if (depthAbs === undefined) {
          return result
        }
        if (settings.negativeZ) {
          depthAbs *= -1
        }
      }

      let markerType: string | undefined
      if (settings.markerTypeColumn !== undefined) {
        column = 'markerType'
        markerType = getString(settings.markerTypeColumn, column)
        if (markerType === undefined) {
          return result
        }
      }

      result.markers.push({ wellId, markerName, markerType, depth, depthAbs })

      return result
    },
    {
      markers: [],
      errors: [],
    },
  )
}
